/**
 * ?? SYNC STATUS INDICATOR v1.3.6
 * 
 * OPTION H: Smart button with intelligent cooldown
 * - Shows countdown to next push/pull
 * - Displays pending changes count
 * - Manual sync button with 1-minute cooldown
 * - Professional UI without playful icons
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { syncService } from '../../core/services/syncService';
import { RefreshCw, Cloud, CloudOff, AlertCircle, Check, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { getBrandLocale } from '../utils/brandCurrency';

const MANUAL_SYNC_COOLDOWN = 60000; // 1 minute in milliseconds

const SyncStatusIndicator = () => {
  const { t } = useTranslation();
  const [syncStatus, setSyncStatus] = useState({
    enabled: false,
    syncing: false,
    lastSync: null,
    pendingChanges: 0,
    error: null,
    nextPushTime: null,
    nextPullCheckTime: null
  });
  const [showDetails, setShowDetails] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [lastManualSync, setLastManualSync] = useState(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  useEffect(() => {
    // Check if sync is enabled (from localStorage, always works offline)
    const checkSyncEnabled = () => {
      try {
        const license = JSON.parse(localStorage.getItem('qawaem_license') || '{}');
        return license.licenseMode === 'ONLINE' && license.companyId;
      } catch {
        return false;
      }
    };

    const isEnabled = checkSyncEnabled();
    
    // Always set enabled state from localStorage (works offline)
    setSyncStatus(prev => ({ 
      ...prev, 
      enabled: isEnabled,
      offline: !navigator.onLine 
    }));

    // If sync not enabled at all (OFFLINE license), don't show indicator
    if (!isEnabled) {
      return;
    }

    // Even if offline, set up listeners (they'll work when back online)

    // Sync event listener
    const handleSyncEvent = (event, data) => {
      console.log('Sync event:', event, data);

      switch (event) {
        case 'sync_started':
        case 'push_started':
        case 'force_sync_started':
          setSyncStatus(prev => ({ ...prev, syncing: true, error: null }));
          break;
        
        case 'sync_completed':
        case 'push_completed':
          setSyncStatus(prev => ({
            ...prev,
            syncing: false,
            lastSync: data.timestamp,
            pendingChanges: 0,
            nextPushTime: null,
            error: null
          }));
          break;
        
        case 'pull_check_completed':
          setSyncStatus(prev => ({
            ...prev,
            lastSync: data.timestamp
          }));
          break;
        
        case 'local_change_detected':
        case 'push_scheduled':
          setSyncStatus(prev => ({
            ...prev,
            pendingChanges: data.pendingCount || prev.pendingChanges + 1,
            nextPushTime: data.nextPushTime
          }));
          break;
        
        case 'sync_error':
        case 'push_error':
        case 'pull_check_error':
          setSyncStatus(prev => ({
            ...prev,
            syncing: false,
            error: data.error
          }));
          break;
        
        default:
          break;
      }
    };

    // Register listener
    syncService.addListener(handleSyncEvent);

    // Load initial status (non-blocking, keep indicator visible even if fails)
    syncService.getSyncStatus().then(status => {
      setSyncStatus(prev => ({
        ...prev,
        ...status,
        enabled: isEnabled // Always preserve enabled from localStorage
      }));
    }).catch(err => {
      console.log('?? Could not load sync status (will retry):', err.message);
      // Don't hide indicator on error - keep it visible
      setSyncStatus(prev => ({
        ...prev,
        enabled: isEnabled, // Keep enabled
        error: t('common.sync_initializing')
      }));
    });

    // Monitor online/offline events
    const handleOnlineStatus = () => {
      setSyncStatus(prev => ({ ...prev, offline: !navigator.onLine }));
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    // Cleanup
    return () => {
      syncService.removeListener(handleSyncEvent);
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  // Countdown timer for next auto-sync
  useEffect(() => {
    const updateCountdown = () => {
      const now = Date.now();
      
      if (syncStatus.nextPushTime && syncStatus.nextPushTime > now) {
        const diff = syncStatus.nextPushTime - now;
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setCountdown({ type: 'push', minutes, seconds });
      } else if (syncStatus.nextPullCheckTime && syncStatus.nextPullCheckTime > now) {
        const diff = syncStatus.nextPullCheckTime - now;
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setCountdown({ type: 'pull', minutes, seconds });
      } else {
        setCountdown(null);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [syncStatus.nextPushTime, syncStatus.nextPullCheckTime]);

  // Cooldown timer for manual sync button
  useEffect(() => {
    if (!lastManualSync) {
      setCooldownRemaining(0);
      return;
    }

    const updateCooldown = () => {
      const now = Date.now();
      const timeSinceLastSync = now - lastManualSync;
      const remaining = Math.max(0, MANUAL_SYNC_COOLDOWN - timeSinceLastSync);
      
      setCooldownRemaining(remaining);
      
      if (remaining === 0) {
        return; // Stop updating when cooldown is over
      }
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);

    return () => clearInterval(interval);
  }, [lastManualSync]);

  const handleManualSync = async () => {
    // Check if already syncing
    if (syncStatus.syncing) return;
    
    // Check cooldown
    if (cooldownRemaining > 0) {
      console.log('? Cooldown active, please wait');
      return;
    }
    
    try {
      setLastManualSync(Date.now());
      const result = await syncService.forceSyncNow();
      if (!result.success) {
        console.error('Manual sync failed:', result.message);
      }
    } catch (error) {
      console.error('Manual sync error:', error);
    }
  };

  const isButtonDisabled = () => {
    return syncStatus.syncing || cooldownRemaining > 0;
  };

  // Don't render if sync is not enabled
  if (!syncStatus.enabled) {
    return null;
  }

  const getStatusColor = () => {
    if (!navigator.onLine || syncStatus.offline) return '#6b7280'; // Gray (offline)
    if (syncStatus.error) return '#ef4444'; // Red
    if (syncStatus.syncing) return '#3b82f6'; // Blue
    if (syncStatus.pendingChanges > 0) return '#f59e0b'; // Orange
    if (syncStatus.lastSync) return '#10b981'; // Green
    return 'rgba(255, 255, 255, 0.5)'; // Gray
  };

  const getStatusIcon = () => {
    if (!navigator.onLine || syncStatus.offline) return <CloudOff size={16} />;
    if (syncStatus.error) return <AlertCircle size={16} />;
    if (syncStatus.syncing) return <RefreshCw size={16} className="sync-spin" />;
    if (syncStatus.pendingChanges > 0) return <Clock size={16} />;
    if (syncStatus.lastSync) return <Check size={16} />;
    return <Cloud size={16} />;
  };

  const getStatusText = () => {
    if (!navigator.onLine || syncStatus.offline) {
      if (syncStatus.pendingChanges > 0) {
        return t('common.sync_offline_with_count', { count: syncStatus.pendingChanges });
      }
      return t('common.sync_offline');
    }
    if (syncStatus.syncing) return t('common.sync_syncing');
    if (syncStatus.pendingChanges > 0) {
      return t('common.sync_pending_count', { count: syncStatus.pendingChanges });
    }
    return t('common.sync_short');
  };

  const formatLastSync = () => {
    if (!syncStatus.lastSync) return t('common.sync_never');
    
    const now = new Date();
    const lastSyncDate = new Date(syncStatus.lastSync);
    const diffMs = now - lastSyncDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return t('common.sync_just_now');
    if (diffMins === 1) return t('common.sync_minute_ago');
    if (diffMins < 60) return t('common.sync_minutes_ago', { count: diffMins });
    if (diffHours === 1) return t('common.sync_hour_ago');
    if (diffHours < 24) return t('common.sync_hours_ago', { count: diffHours });
    
    return lastSyncDate.toLocaleString(getBrandLocale());
  };

  const formatCountdown = () => {
    if (!countdown) return null;
    const { type, minutes, seconds } = countdown;
    const label = type === 'push' ? t('common.sync_push_in') : t('common.sync_pull_in');
    return `${label} ${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatCooldown = () => {
    if (cooldownRemaining === 0) return null;
    const seconds = Math.ceil(cooldownRemaining / 1000);
    return t('common.sync_auto_in', { seconds });
  };

  return (
    <div 
      style={{ 
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}
      onMouseEnter={() => setShowDetails(true)}
      onMouseLeave={() => setShowDetails(false)}
    >
      {/* Sync Status Button */}
      <motion.button
        onClick={handleManualSync}
        disabled={isButtonDisabled()}
        whileHover={{ scale: isButtonDisabled() ? 1 : 1.05 }}
        whileTap={{ scale: isButtonDisabled() ? 1 : 0.95 }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          background: syncStatus.error 
            ? 'rgba(239, 68, 68, 0.1)' 
            : syncStatus.pendingChanges > 0
            ? 'rgba(245, 158, 11, 0.1)'
            : 'rgba(255, 255, 255, 0.05)',
          border: `1px solid ${getStatusColor()}`,
          borderRadius: '8px',
          color: getStatusColor(),
          cursor: isButtonDisabled() ? 'not-allowed' : 'pointer',
          fontSize: '0.875rem',
          transition: 'all 0.2s ease',
          opacity: isButtonDisabled() ? 0.6 : 1
        }}
      >
        <span style={{ color: getStatusColor() }}>
          {getStatusIcon()}
        </span>
        <span>
          {getStatusText()}
        </span>
      </motion.button>

      {/* Hover Details */}
      {showDetails && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '0.5rem',
            background: 'linear-gradient(135deg, rgba(30, 30, 40, 0.98), rgba(40, 40, 50, 0.98))',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '1rem',
            minWidth: '320px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            zIndex: 1000
          }}
        >
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ 
              fontSize: '0.875rem', 
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '0.5rem'
            }}>
              {t('common.sync_status')}
            </div>
            <div style={{ 
              fontSize: '1rem', 
              color: getStatusColor(),
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {getStatusIcon()}
              {!navigator.onLine || syncStatus.offline
                ? t('common.sync_offline')
                : syncStatus.syncing
                ? t('common.sync_syncing')
                : syncStatus.error
                ? t('common.error')
                : syncStatus.pendingChanges > 0
                ? t('common.sync_pending_changes')
                : t('common.sync_synced')}
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '0.5rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              fontSize: '0.875rem'
            }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                {t('common.sync_last_sync')}:
              </span>
              <span style={{ color: 'white' }}>
                {formatLastSync()}
              </span>
            </div>

            {syncStatus.pendingChanges > 0 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                fontSize: '0.875rem'
              }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {t('common.sync_pending_changes')}:
                </span>
                <span style={{ 
                  color: '#f59e0b',
                  fontWeight: '600'
                }}>
                  {syncStatus.pendingChanges}
                </span>
              </div>
            )}

            {countdown && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                fontSize: '0.875rem'
              }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {t('common.sync_next')}:
                </span>
                <span style={{ 
                  color: '#3b82f6',
                  fontWeight: '600',
                  fontFamily: 'monospace'
                }}>
                  {formatCountdown()}
                </span>
              </div>
            )}

            {syncStatus.totalDevices > 0 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                fontSize: '0.875rem'
              }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {t('common.sync_connected_devices')}:
                </span>
                <span style={{ color: 'white' }}>
                  {syncStatus.totalDevices}
                </span>
              </div>
            )}

            {syncStatus.error && (
              <div style={{ 
                marginTop: '0.5rem',
                padding: '0.5rem',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '6px',
                fontSize: '0.75rem',
                color: '#ef4444'
              }}>
                {syncStatus.error}
              </div>
            )}
          </div>

          <div style={{ 
            marginTop: '0.75rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            fontSize: '0.75rem',
            color: 'rgba(255, 255, 255, 0.5)',
            textAlign: 'center'
          }}>
            {t('common.sync_schedule_hint')}
          </div>

          {/* Manual Sync Button */}
          <motion.button
            onClick={handleManualSync}
            disabled={isButtonDisabled()}
            whileHover={{ scale: isButtonDisabled() ? 1 : 1.02 }}
            whileTap={{ scale: isButtonDisabled() ? 1 : 0.98 }}
            style={{
              marginTop: '0.75rem',
              width: '100%',
              padding: '0.625rem',
              background: cooldownRemaining > 0 
                ? 'rgba(107, 114, 128, 0.5)' 
                : 'linear-gradient(135deg, #3b82f6, #2563eb)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: isButtonDisabled() ? 'not-allowed' : 'pointer',
              opacity: isButtonDisabled() ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {cooldownRemaining > 0 ? (
              <>
                <Clock size={14} />
                {formatCooldown()}
              </>
            ) : (
              <>
                <RefreshCw size={14} className={syncStatus.syncing ? 'sync-spin' : ''} />
                {syncStatus.syncing ? t('common.sync_syncing') : t('common.sync_now')}
              </>
            )}
          </motion.button>
        </motion.div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .sync-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default SyncStatusIndicator;


